<template>
  <Dashboard :skip-onboarding="true">
    <div class="max-w-7xl mx-auto flex flex-col gap-9">
      <div v-if="notFound" class="max-w-xl flex flex-col gap-4">
        <h1 class="text-[28px] font-semibold leading-tight text-text-primary">{{ $t('play.lan_party.expired') }}</h1>
        <router-link to="/matchmaking" class="self-start px-5 py-3 bg-primary-600 rounded-lg text-sm font-semibold text-white hover:bg-primary-500">
          {{ $t('play.lan_party.expired_cta') }}
        </router-link>
      </div>
      <p v-if="loadError" class="text-danger-400">{{ $t('play.lan_party.network_error') }}</p>

      <div v-if="creator" class="flex flex-wrap items-center justify-between gap-4">
        <h1 class="text-3xl sm:text-4xl font-bold text-text-primary">{{ $t('play.matchmaking.title') }}</h1>
        <LanNavPills v-if="!auth.user()?.matchmaking_lan_only" />
      </div>

      <!-- The night is on -->
      <div
        v-if="creator && lanParty"
        class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start"
      >
        <LanHero :creator="creator" class="min-w-0" />

        <div class="flex flex-col gap-4 self-start lg:row-span-2 lg:sticky lg:top-4">
          <!-- Not in yet -->
          <aside
            v-if="!membership"
            class="border-t border-gray-800 pt-6 lg:border-t-0 lg:pt-6 lg:bg-gray-900 lg:border lg:rounded-lg lg:p-6 flex flex-col gap-5"
          >
            <div class="flex items-center gap-3">
              <LanAvatar :src="creator.picture_url" size="w-12 h-12 text-lg" />
              <div class="min-w-0">
                <p class="text-xs text-text-secondary">{{ $t('play.lan_party.live_for', { time: liveFor }) }}</p>
                <h1 class="text-text-primary font-semibold text-[22px] leading-tight">{{ $t('play.lan_party.play_in', { name: creator.name }) }}</h1>
              </div>
            </div>
            <p class="text-sm text-text-secondary">{{ $t('play.lan_party.invite_body', { name: creator.name, mode: lanParty.play_mode?.friendly_name || $t('play.lan_party.matches') }) }}</p>
            <LanChips :lan-party="lanParty" />
            <router-link
              v-if="managesThis"
              :to="{ name: 'LanControlRoomPage', params: { slug } }"
              class="w-full py-4 px-4 rounded-lg text-base font-semibold text-white bg-primary-600 hover:bg-primary-500 text-center"
            >{{ $t('play.lan_party.open_control_room') }}</router-link>
            <button
              v-if="managesThis"
              type="button"
              :disabled="joining"
              @click="join()"
              class="w-full py-3 px-4 rounded-lg text-sm font-semibold text-text-primary bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
            >{{ $t('play.lan_party.manager_play_tonight') }}</button>
            <button
              v-else
              type="button"
              :disabled="joining"
              @click="join()"
              class="w-full py-4 px-4 rounded-lg text-base font-semibold text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <UsersIcon
                class="w-5 h-5"
                aria-hidden="true"
              />{{ joining ? $t('play.lan_party.joining') : $t('play.lan_party.join') }}
            </button>
            <p
              v-if="queueError"
              class="text-sm text-danger-400"
              aria-live="polite"
            >{{ queueError }}</p>
          </aside>

          <!-- In -->
          <aside
            v-else
            class="border-t border-gray-800 pt-6 lg:border-t-0 lg:pt-6 lg:bg-gray-900 lg:border lg:rounded-lg lg:p-6 flex flex-col gap-5"
          >
            <div class="flex items-center gap-3">
              <LanAvatar :src="auth.user()?.steam_profile?.avatarfull || auth.user()?.twitch?.avatar_url" size="w-12 h-12 text-lg" />
              <p class="text-lg font-semibold text-white min-w-0 truncate">{{ username }}</p>
            </div>
            <LanChips :lan-party="lanParty" />
            <div v-if="!onCreatorSide" class="flex flex-col gap-2" role="radiogroup">
              <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.seat_choice') }}</p>
              <button
                v-for="choice in [true, false]"
                :key="String(choice)"
                type="button"
                role="radio"
                :aria-checked="membership.wait_for_creator === choice"
                :disabled="!!activeLobby"
                @click="chooseSeat(choice)"
                class="text-left rounded-lg border p-3 flex gap-3 disabled:opacity-60"
                :class="membership.wait_for_creator === choice ? 'border-secondary-500 bg-secondary-950/40' : 'border-gray-800 hover:border-gray-600'"
              >
                <span
                  class="mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0"
                  :class="membership.wait_for_creator === choice ? 'border-secondary-400 bg-secondary-400' : 'border-gray-600'"
                />
                <span class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-text-primary">{{ choice ? $t('play.lan_party.wait_for_creator', { name: creator.name }) : $t('play.lan_party.play_now_with_viewers') }}</span>
                  <span class="text-xs text-text-secondary">{{ choice ? $t('play.lan_party.wait_hint', { name: creator.name, games: membership.games_with_creator ?? 0 }) : $t('play.lan_party.play_now_hint') }}</span>
                </span>
              </button>
            </div>

            <div
              v-if="showTwitch"
              class="flex items-center gap-3 border-y border-gray-800 py-4"
            >
              <TwitchIcon class="w-5 h-5 flex-shrink-0" style="color:#BF94FF" />
              <p class="flex-1 text-xs text-text-secondary">{{ $t('play.lan_party.twitch_link_prompt', { name: creator.name }) }}</p>
              <button
                type="button"
                :disabled="linkingTwitch"
                @click="linkTwitch"
                class="flex-shrink-0 text-xs font-semibold text-white px-2.5 py-1.5 rounded-md disabled:opacity-50"
                style="background:#9146FF"
              >{{ $t('play.lan_party.twitch_link') }}</button>
            </div>

            <router-link
              v-if="activeLobby"
              :to="{ name: 'MatchmakingLobbyPage', params: { id: activeLobby.uuid } }"
              class="w-full py-4 px-4 rounded-lg bg-green-700 text-white text-base font-semibold text-center hover:bg-green-600"
            >{{ $t('play.matchmaking.go_to_matchroom') }}</router-link>
            <template v-else-if="isQueued">
              <div class="flex items-center gap-3 rounded-lg border border-secondary-700/60 bg-gray-950 px-4 py-3">
                <span class="w-4 h-4 rounded-full border-2 border-secondary-300 border-t-transparent animate-spin flex-shrink-0" />
                <span class="text-xs uppercase tracking-widest text-secondary-300 font-bold flex-1">
                  {{ pickedForSeat ? $t('play.lan_party.you_got_picked') : $t('play.lan_party.queueing') }}
                </span>
                <span class="font-mono text-xs text-text-secondary tabular-nums">{{ elapsedLabel }}</span>
                <button
                  type="button"
                  class="p-1 -mr-1 rounded-md text-text-secondary hover:text-text-primary hover:bg-gray-900"
                  :aria-pressed="!searchingSoundOn"
                  :aria-label="searchingSoundOn ? $t('play.matchmaking.searching_sound_off') : $t('play.matchmaking.searching_sound_on')"
                  @click="toggleSearchingSound"
                >
                  <SpeakerWaveIcon v-if="searchingSoundOn" class="w-4 h-4" aria-hidden="true" />
                  <SpeakerXMarkIcon v-else class="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                @click="cancelMatchmaking"
                class="w-full py-2.5 px-4 rounded-lg border border-gray-700 text-text-primary text-sm font-semibold hover:bg-gray-800"
              >{{ $t('play.lan_party.cancel_queue') }}</button>
            </template>
            <template v-else-if="onCreatorSide">
              <p class="text-sm text-text-secondary">{{ $t('play.lan_party.manager_playing', { name: creator.name }) }}</p>
              <router-link
                :to="{ name: 'LanControlRoomPage', params: { slug } }"
                class="w-full py-4 px-4 rounded-lg text-base font-semibold text-white bg-primary-600 hover:bg-primary-500 text-center"
              >{{ $t('play.lan_party.open_control_room') }}</router-link>
              <button
                type="button"
                :disabled="joining"
                @click="sitOut"
                class="w-full py-3 px-4 rounded-lg text-sm font-semibold text-text-primary bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
              >{{ $t('play.lan_party.sit_out') }}</button>
            </template>
            <template v-else>
              <button
                type="button"
                :disabled="queueing || !lanReady || !party"
                @click="queueNow"
                class="w-full py-4 px-4 rounded-lg text-base font-semibold text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 flex items-center justify-center gap-2"
              ><UsersIcon class="w-5 h-5" aria-hidden="true" />{{ $t('play.lan_party.queue_now') }}</button>
              <button
                type="button"
                :disabled="joining"
                @click="leaveLan"
                class="w-full py-2.5 px-4 rounded-lg border border-gray-700 text-text-primary text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
              >{{ $t('play.lan_party.leave_lan') }}</button>
            </template>
            <p
              v-if="queueError"
              class="text-sm text-danger-400"
              aria-live="polite"
            >{{ queueError }}</p>
            <p
              v-else-if="!lanReady && !isQueued"
              class="text-sm text-text-secondary"
            >{{ $t('play.lan_party.not_ready', { name: creator.name }) }}</p>
            <p v-else class="flex items-start gap-2 text-xs text-text-secondary">
              <GlobeAltIcon class="w-4 h-4 flex-shrink-0 mt-px" aria-hidden="true" />
              <span>{{ $t('play.lan_party.queue_note', { region: lanParty.region.name }) }}</span>
            </p>
          </aside>
        </div>

        <LanLeaderboard
          :slug="slug"
          :creator-name="creator.name"
          class="min-w-0 lg:col-start-1"
          @me="onMe"
        />
      </div>

      <!-- The night is over -->
      <div v-else-if="creator && !loadError" class="max-w-5xl flex flex-col gap-8">
        <div class="flex flex-col md:flex-row md:items-center gap-4">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <LanAvatar :src="creator.picture_url" size="w-14 h-14 text-xl" />
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('play.lan_party.night_over', { name: creator.name }) }}</p>
              <h1 class="text-[28px] font-semibold leading-tight text-text-primary">{{ $t('play.lan_party.gg', { name: username }) }}</h1>
              <p
                v-if="creator.last_lan_party"
                class="text-text-secondary text-sm"
              >{{ $t('play.lan_party.night_summary', { n: creator.last_lan_party.matches_played }) }}</p>
            </div>
          </div>
          <button
            type="button"
            :disabled="following"
            @click="follow"
            class="self-start md:self-center flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 text-text-primary text-sm font-semibold hover:bg-gray-700 disabled:opacity-70"
          >
            <BellIcon class="w-4 h-4" aria-hidden="true" />
            {{ following ? $t('play.lan_party.ended_notify_done') : $t('play.lan_party.ended_notify', { name: creator.name }) }}
          </button>
        </div>

        <div
          v-if="night"
          class="rounded-xl border border-primary-800 user-score-gradient p-5 md:p-6 flex flex-col gap-4"
        >
          <div class="flex flex-wrap items-center gap-3">
            <LanAvatar :src="night.avatar" size="w-10 h-10 text-sm" />
            <p class="text-lg font-semibold flex-1 text-text-primary">{{ $t('play.lan_party.your_night') }}</p>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-px rounded-lg overflow-hidden border border-gray-800 bg-gray-800">
            <div class="bg-gray-900 p-4"><p class="text-2xl font-semibold tabular-nums">{{ night.games }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.col_matches') }}</p></div>
            <div class="bg-gray-900 p-4"><p class="text-2xl font-semibold tabular-nums">{{ night.kills }} / {{ night.deaths }} / {{ night.assists }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.kda') }}</p></div>
            <div class="bg-gray-900 p-4"><p class="text-2xl font-semibold tabular-nums">{{ night.wins }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.wins') }}</p></div>
            <div class="bg-gray-900 p-4"><p class="text-2xl font-semibold tabular-nums">{{ night.points }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.col_points') }}</p></div>
          </div>
          <p class="text-xs text-text-secondary">{{ $t('play.lan_party.weekly_note', { name: creator.name }) }}</p>
        </div>

        <section class="flex flex-col gap-3">
          <h2 class="text-xl font-semibold text-text-primary">{{ subscriber ? $t('play.lan_party.night_not_over') : $t('play.lan_party.ended_keep_playing') }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              v-if="!subscriber"
              class="bg-gray-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-3"
            >
              <span class="w-10 h-10 rounded-lg bg-primary-950 border border-primary-800 flex items-center justify-center text-primary-300"><AcademicCapIcon class="w-5 h-5" aria-hidden="true" /></span>
              <p class="font-semibold text-text-primary">{{ $t('play.lan_party.card_coach_title') }}</p>
              <p class="text-sm text-text-secondary flex-1">{{ $t('play.lan_party.card_coach_body', { name: creator.name }) }}</p>
              <router-link
                to="/coach"
                class="self-start px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-500"
              >{{ $t('play.lan_party.card_coach_cta') }}</router-link>
            </div>
            <div class="bg-gray-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-3">
              <span class="w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center"><img v-if="lastMap" :src="pinUrl(lastMap)" alt="" class="w-7 h-7 object-contain" /><PlayIcon v-else class="w-5 h-5 text-text-primary" aria-hidden="true" /></span>
              <p class="font-semibold text-text-primary">{{ $t('play.lan_party.card_server_title') }}</p>
              <p class="text-sm text-text-secondary flex-1">{{ $t('play.lan_party.card_server_body') }}</p>
              <router-link
                to="/"
                class="self-start px-4 py-2.5 rounded-lg bg-gray-800 text-text-primary text-sm font-semibold hover:bg-gray-700"
              >{{ $t('play.lan_party.card_server_cta') }}</router-link>
            </div>
            <div class="bg-gray-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-3">
              <span class="w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-text-primary"><UsersIcon class="w-5 h-5" aria-hidden="true" /></span>
              <p class="font-semibold text-text-primary">{{ $t('play.lan_party.card_mm_title') }}</p>
              <p class="text-sm text-text-secondary flex-1">{{ $t('play.lan_party.card_mm_body') }}</p>
              <router-link
                to="/matchmaking"
                class="self-start px-4 py-2.5 rounded-lg text-sm font-semibold"
                :class="subscriber ? 'bg-primary-600 text-white hover:bg-primary-500' : 'bg-gray-800 text-text-primary hover:bg-gray-700'"
              >{{ $t('play.lan_party.queue_now') }}</router-link>
            </div>
          </div>
        </section>

        <div
          v-if="!subscriber && creator.trial_code"
          class="rounded-xl p-px"
          style="background:linear-gradient(120deg,#C44CB6,#4E54C8)"
        >
          <div class="rounded-xl bg-gray-950 draw-glow p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <LanAvatar :src="creator.picture_url" size="w-12 h-12 text-lg" />
            <div class="flex-1 min-w-0">
              <p class="text-xs uppercase tracking-widest text-secondary-300 font-bold">{{ $t('play.lan_party.gift_eyebrow', { name: creator.name }) }}</p>
              <p class="text-lg font-semibold text-text-primary">{{ $t('play.lan_party.gift_title', { name: creator.name }) }}</p>
              <p class="text-sm text-text-secondary">
                {{ $t('play.lan_party.gift_body') }}
                <span class="font-mono text-text-primary bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5">{{ creator.trial_code }}</span>
              </p>
            </div>
            <router-link
              :to="{ name: 'Redeem', query: { code: creator.trial_code } }"
              class="px-5 py-3 rounded-lg bg-primary-600 text-white text-base font-semibold hover:bg-primary-500 whitespace-nowrap"
            >
              {{ $t('play.lan_party.gift_cta', { name: creator.name }) }}
            </router-link>
          </div>
        </div>

        <LanLeaderboard
          :slug="slug"
          :creator-name="creator.name"
          :initial-limit="3"
          :poll-ms="0"
          @me="onMe"
        />
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import { useHead } from '@unhead/vue';
import {
  ref, computed, watch, onBeforeUnmount,
} from 'vue';
import axios from 'axios';
import moment from 'moment';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  UsersIcon, BellIcon, AcademicCapIcon, PlayIcon,
} from '@heroicons/vue/24/solid';
import { GlobeAltIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/vue/24/outline';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import LanHero from '../components/LanParty/LanHero.vue';
import LanChips from '../components/LanParty/LanChips.vue';
import LanAvatar from '../components/LanParty/LanAvatar.vue';
import LanLeaderboard from '../components/LanParty/LanLeaderboard.vue';
import LanNavPills from '../components/LanParty/LanNavPills.vue';
import { useQueueSearch } from '../composables/useMatchmakingQueue';
import useAudioSettings from '../composables/useAudioSettings';
import { measuredPings } from '../utils/server_locations';
import TwitchIcon from '../components/LanParty/TwitchIcon.vue';
import { apiError, pinUrl, startTwitchLink } from '../utils/lan_party';

const POLL_MS = 15_000;

const { t } = useI18n();
const auth = useAuth();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { slug } = route.params;
const {
  party, isQueued, activeLobby, elapsedLabel, cancelMatchmaking,
} = useQueueSearch();

const creator = ref(null);
useHead({ title: computed(() => creator.value && t('play.lan_party.page_title', { name: creator.value.name })) });
const loadError = ref(false);
const notFound = ref(false);
const night = ref(null);
const membership = ref(null);
const joining = ref(false);
const queueing = ref(false);
const queueError = ref('');
const following = ref(false);
let pollTimer;
let unmounted = false;

const lanParty = computed(() => creator.value?.lan_party || null);
const lanReady = computed(() => !!lanParty.value?.play_mode && !!lanParty.value?.cs_map);
const subscriber = computed(() => (auth.user()?.access_level || 0) > 0);
const managesThis = computed(() => store.getters.matchmakingContext?.creator?.slug === slug);
// A manager either runs the Creator side or queues like any viewer.
// Waiting for a seat and seated by the Creator: the pill says so, to build the hype.
const pickedForSeat = computed(
  () => !!membership.value?.wait_for_creator && !!membership.value?.picked,
);
const onCreatorSide = computed(() => managesThis.value && !!membership.value?.creator_side);
// Only once the Creator's channel is connected: before that nobody could see the viewer anyway.
const linkedTwitch = computed(() => auth.user()?.twitch || null);
// Not on the Creator side: nobody needs to recognise the Creator's own team in their chat.
const showTwitch = computed(() => !!creator.value?.twitch?.connected && !linkedTwitch.value
  && !!auth.user()?.twitch_available && !onCreatorSide.value && !activeLobby.value);
const linkingTwitch = ref(false);
function linkTwitch() {
  linkingTwitch.value = true;
  startTwitchLink().catch((e) => {
    linkingTwitch.value = false;
    queueError.value = apiError(e, t('play.lan_party.twitch_failed'));
  });
}
const { settings: audioSettings, updateSound } = useAudioSettings();
const searchingSoundOn = computed(() => !audioSettings.value.sounds.searching.muted);
function toggleSearchingSound() {
  updateSound('searching', { muted: searchingSoundOn.value });
}
const username = computed(() => auth.user()?.username || auth.user()?.display_name || '');
const lastMap = computed(() => creator.value?.last_lan_party?.cs_map?.name || null);
const liveFor = computed(() => {
  const start = lanParty.value?.started_at;
  if (!start) return '';
  const minutes = Math.max(0, moment().diff(moment(start), 'minutes'));
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
});

function load() {
  axios.get(`/public/creators/${slug}`).then(({ data }) => {
    creator.value = data;
    loadError.value = false;
  }).catch((error) => {
    if (error.response?.status === 404) notFound.value = true;
    else loadError.value = true;
  }).finally(() => {
    clearTimeout(pollTimer);
    if (!unmounted && !notFound.value) pollTimer = setTimeout(load, POLL_MS);
  });
}

// The membership rides on the parties payload the layout already fetches; its creator is the slug,
// the other LAN party payloads carry an object.
function creatorSlug(lan) {
  return lan?.creator?.slug ?? lan?.creator;
}

// Only tonight's membership counts: a Creator who ended and reopened the night keeps the slug,
// and a membership from the ended one would show a queue the backend refuses.
watch(() => [store.getters.matchmakingContext?.lan_party, lanParty.value?.uuid], ([lan, uuid]) => {
  membership.value = lan && creatorSlug(lan) === slug && (!uuid || lan.uuid === uuid) ? lan : null;
}, { immediate: true });

function join(waitForCreator) {
  const uuid = lanParty.value?.uuid;
  if (!uuid) return Promise.resolve();
  joining.value = true;
  queueError.value = '';
  const body = waitForCreator === undefined ? {} : { wait_for_creator: waitForCreator };
  return axios.post(`/matchmaking/lan_parties/${uuid}/members`, body).then(({ data }) => {
    membership.value = { ...data.membership, uuid, creator: slug };
    store.commit('setMatchmakingContext', { ...store.getters.matchmakingContext, lan_party: membership.value });
    // Joining is what grants matchmaking: the layout subscribes once /me says so.
    load();
    return auth.fetch().catch(() => {});
  }).catch((error) => {
    queueError.value = apiError(error, t('play.lan_party.join_failed'));
  }).finally(() => {
    joining.value = false;
  });
}

function chooseSeat(waitForCreator) {
  if (!membership.value || membership.value.wait_for_creator === waitForCreator) return;
  queueError.value = '';
  axios.put(`/matchmaking/lan_parties/${lanParty.value.uuid}/members/${membership.value.id}`, {
    member: { wait_for_creator: waitForCreator },
  }).then(({ data }) => {
    membership.value = { ...membership.value, ...data.membership };
    store.commit('setMatchmakingContext', { ...store.getters.matchmakingContext, lan_party: membership.value });
  }).catch((error) => {
    queueError.value = apiError(error, t('play.lan_party.network_error'));
  });
}

function sitOut() {
  joining.value = true;
  queueError.value = '';
  return axios.delete(`/matchmaking/lan_parties/${lanParty.value.uuid}/members/${membership.value.id}`).then(() => {
    membership.value = null;
    store.commit('setMatchmakingContext', { ...store.getters.matchmakingContext, lan_party: null });
    return true;
  }).catch((error) => {
    queueError.value = apiError(error, t('play.lan_party.network_error'));
    return false;
  }).finally(() => {
    joining.value = false;
  });
}

function leaveLan() {
  // /me, as leaving can take matchmaking away.
  sitOut().then((left) => left && auth.fetch().catch(() => {}));
}

// Arriving from the join link counts as joining: once tonight's night is known, join it, then
// drop ?join so a reload does not join again. Joining twice is harmless, the backend keeps one row.
watch(() => lanParty.value?.uuid, (uuid) => {
  if (!uuid || !route.query.join) return;
  router.replace({ query: {} });
  // A new tab has no parties payload yet: fetch it, so a manager is not joined as a viewer.
  axios.get('/matchmaking/parties').then(({ data }) => {
    store.commit('setMatchmakingContext', data);
  }).catch(() => {}).then(() => {
    if (!membership.value && !managesThis.value) join();
  });
}, { immediate: true });

function queueNow() {
  if (!party.value || !lanParty.value) return;
  queueing.value = true;
  queueError.value = '';
  axios.post(`/matchmaking/parties/${party.value.id}/queue_entries`, {
    queue_entry: { lan_party_uuid: lanParty.value.uuid },
    pings: measuredPings(),
  }).then((response) => {
    store.commit('setMatchmakingParty', { ...party.value, status: 'queued', queue_entry: response.data });
  }).catch((error) => {
    queueError.value = apiError(error, t('play.matchmaking.queue_failed'));
  }).finally(() => {
    queueing.value = false;
  });
}

// The leaderboard's own row, kept for tonight only: the week tab must not rewrite "Your night".
function onMe({ me: row, period }) {
  if (period === 'tonight') night.value = row;
}

function follow() {
  axios.post(`/creators/${slug}/follow`).then(() => {
    following.value = true;
  }).catch(() => {});
}

// Joins, queues and the Creator's mode or map changes arrive over the socket: refetch right away.
function onRoomUpdate(event) {
  if (event.detail !== lanParty.value?.uuid) return;
  load();
  // A pick lands on the membership, which rides on the parties payload.
  axios.get('/matchmaking/parties').then(({ data }) => store.commit('setMatchmakingContext', data)).catch(() => {});
}
window.addEventListener('lan-party-room-update', onRoomUpdate);

onBeforeUnmount(() => {
  unmounted = true;
  clearTimeout(pollTimer);
  window.removeEventListener('lan-party-room-update', onRoomUpdate);
});

load();
</script>

<style scoped>
.draw-glow { background: radial-gradient(60% 90% at 50% 0%, rgba(196, 76, 182, 0.16) 0%, rgba(196, 76, 182, 0) 100%), #080a0d; }
.user-score-gradient { background: linear-gradient(90deg, rgba(78, 84, 200, 0.15) 0%, rgba(78, 84, 200, 0) 100%); }
</style>
